import { useFormik } from 'formik'
import React, { ChangeEvent, useState } from 'react'
import ModalComponent from '..'
import { useModalControl } from '../../../hooks/useModalControl'
import { ListCategoryDoc } from '../../../redux-toolkits/categories/categories.type'
import {
  useCreateProductCategoryMutation,
  useEditProductCategoryMutation,
} from '../../../redux-toolkits/products/products.slice'
import { CreateCategorySchema } from '../../../validation/validate'
import { Button } from '../../button'
import { ImageUploadButton } from '../../button/imageUploadButton'
import Icon from '../../icon'
import { ImageBanner } from '../../images/imageBanner'
import Dropdown from '../../inputs/dropdown'
import { EditInput } from '../../inputs/editInput'
import { TextInput } from '../../inputs/textInput'
import { AudienceFields } from '../../product/audience-fields'
import {
  categorySectionOptions,
  getCategorySectionLabel,
} from '../../product/category-section'
import {
  CategoryFormValues,
  getCategoryInitialValues,
  toCategoryPayload,
} from './category-form-values'
import { ImageUploadPreviewModal } from './ImagePreviewModal'

const statuses = [
  { text: 'Active', value: true },
  { text: 'Inactive', value: false },
]

export const AddCategoryModal: React.FC<{
  open: boolean
  setOpen: (cal: boolean) => void
  onCloseCallback?: () => void
  onSuccessCallback?: (cat: ListCategoryDoc) => void
  mode: 'edit' | 'create'
  category?: ListCategoryDoc
}> = ({
  open,
  setOpen,
  onCloseCallback = () => {},
  onSuccessCallback = () => {},
  mode,
  category,
}) => {
  const [uploadedIcon, setUploadedIcon] = useState<File>()
  const [editIndex, setEditIndex] = useState<number>()

  const [createCategory, { isLoading: creating }] =
    useCreateProductCategoryMutation()
  const [editCategory, { isLoading: editing }] =
    useEditProductCategoryMutation()

  const { handleModalOpen, modal, modalOpen, setModalOpen, handleModalClose } =
    useModalControl()

  const saveCategory = (values: CategoryFormValues) => {
    const payload = toCategoryPayload(values)
    if (mode === 'create') {
      return createCategory({ ...payload, isActive: undefined })
    }
    if (category) {
      return editCategory({
        id: category._id,
        payload: { ...payload, icon: payload.icon || undefined },
      })
    }
  }

  const handleFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files) {
      return
    }

    const selectedFile = event.target.files[0]

    setUploadedIcon(selectedFile)

    handleModalOpen('imageUploadPreviewModal')
  }

  const {
    values,
    errors,
    handleSubmit,
    handleChange,
    isValid,
    resetForm,
    setFieldValue,
    handleBlur,
  } = useFormik({
    initialValues: getCategoryInitialValues(category),
    enableReinitialize: true,
    validationSchema: CreateCategorySchema,
    onSubmit: async (values) => {
      const resp = await saveCategory(values)
      if (!resp?.data?.success) return
      resetForm()
      setOpen(false)
      onSuccessCallback(resp.data.data)
    },
  })
  return (
    <ModalComponent
      open={open}
      setOpen={setOpen}
      onCloseCallback={() => {
        resetForm()
        onCloseCallback()
      }}
      title={mode === 'create' ? 'Add Category' : 'Manage Category'}
      childrenClassName="md:!max-w-[420px]"
    >
      <form
        className="flex flex-col gap-6 justify-center items-center"
        onSubmit={handleSubmit}
      >
        {mode === 'create' && (
          <>
            {values.icon ? (
              <ImageBanner
                imageUrl={values.icon}
                loading={false}
                className="
                !rounded-full
                !w-[120px] !max-h-[120px]
                !bg-contain
                "
                errors={errors.icon ? errors.icon : ''}
                onImageEdit={(e) => {
                  handleFileChange(e)
                }}
              />
            ) : (
              <ImageUploadButton
                handleChange={(e) => {
                  handleFileChange(e)
                }}
                showText
                icon={'pic-icon'}
                iconDim={52}
                text={`Upload category svg`}
                className="
                !relative !w-full
                !m-0 !top-0 !right-0
                !h-fit
                !rounded-lg
                !col-span-full
                "
              />
            )}
          </>
        )}

        <EditInput
          title="Category name"
          value={values.name}
          onChange={handleChange}
          type="text"
          placeholder="Category name"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 1}
          onEditClick={() => {
            setEditIndex(1)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('name', '')
            } else {
              setFieldValue('name', category?.name)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="name"
          mode={mode === 'create' ? 'edit' : 'edit'}
        />

        <EditInput
          title="Category description"
          value={values.description}
          onChange={handleChange}
          type="textarea"
          placeholder="Category description"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 2}
          onEditClick={() => {
            setEditIndex(2)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('description', '')
            } else {
              setFieldValue('description', category?.description)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="description"
          mode={mode === 'create' ? 'edit' : 'edit'}
        />

        <EditInput
          title="Combo tag"
          value={values.comboTagLine}
          onChange={handleChange}
          type="textarea"
          placeholder="Combo tag"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 3}
          onEditClick={() => {
            setEditIndex(3)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('comboTagLine', '')
            } else {
              setFieldValue('comboTagLine', category?.comboTagLine)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="comboTagLine"
          mode={mode === 'create' ? 'edit' : 'edit'}
        />

        <EditInput
          title="Recommended tag"
          value={values.recommendedTagLine}
          onChange={handleChange}
          type="textarea"
          placeholder="Recommended tag"
          onEditSave={() => {
            setEditIndex(undefined)
          }}
          editing={editIndex === 4}
          onEditClick={() => {
            setEditIndex(4)
          }}
          onCancel={() => {
            if (mode === 'create') {
              setFieldValue('recommendedTagLine', '')
            } else {
              setFieldValue('recommendedTagLine', category?.recommendedTagLine)
            }
            setEditIndex(undefined)
          }}
          loading={false}
          inputName="recommendedTagLine"
          mode={mode === 'create' ? 'edit' : 'edit'}
        />

        <Dropdown
          className="w-full"
          menuClassName="!max-h-[200px]"
          position="bottom start"
          menuButton={
            <TextInput
              type="text"
              title="Section"
              name="section"
              placeholder="Select section"
              value={getCategorySectionLabel(values.section) ?? ''}
              onBlur={handleBlur}
              onChange={handleChange}
              hasIcon={false}
              readOnly
              extra={
                <Icon
                  id={'left-caret'}
                  width={16}
                  height={16}
                  className="-rotate-90 text-sec-black"
                />
              }
            />
          }
          menuItems={categorySectionOptions.map((section) => ({
            name: section.text,
            value: section.value,
            onClick: () => {
              setFieldValue('section', section.value)
            },
          }))}
        />

        <div className="w-full">
          <AudienceFields
            contentLabel="category"
            ageRange={values.ageRange}
            genders={values.genders}
            onAgeChange={(range) => {
              void setFieldValue('ageRange', range)
            }}
            onGendersChange={(genders) => {
              void setFieldValue('genders', genders)
            }}
          />
        </div>

        {mode === 'edit' && (
          <Dropdown
            className="w-full"
            menuClassName="!max-h-[200px]"
            position="bottom start"
            menuButton={
              <TextInput
                type="text"
                title="Status"
                name="status"
                placeholder="Select status"
                value={values.isActive ? 'Active' : 'Inactive'}
                onBlur={handleBlur}
                onChange={handleChange}
                hasIcon={false}
                readOnly
                extra={
                  <Icon
                    id={'left-caret'}
                    width={16}
                    height={16}
                    className="-rotate-90 text-sec-black"
                  />
                }
              />
            }
            menuItems={statuses.map((status) => {
              return {
                name: status.text,
                value: status.value,
                onClick: () => {
                  setFieldValue('isActive', status.value)
                },
              }
            })}
          />
        )}

        <Button
          label="Save"
          type="submit"
          className="mt-2 w-full"
          disabled={!isValid || creating || editing}
          loading={creating || editing}
          primary
        />
      </form>

      {uploadedIcon ? (
        <ImageUploadPreviewModal
          image={uploadedIcon as File}
          open={modal === 'imageUploadPreviewModal' && modalOpen}
          setOpen={setModalOpen}
          onCloseCallback={() => {
            setUploadedIcon(undefined)
          }}
          onSuccessCallback={(url) => {
            setFieldValue('icon', url)
          }}
        />
      ) : null}
    </ModalComponent>
  )
}
